import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
  Grid,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Snackbar,
  InputAdornment,
  Divider,
  Collapse
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Refresh as RefreshIcon,
  Payment as PaymentIcon,
  MonetizationOn as PaidIcon,
  Schedule as PendingIcon,
  Calculate as CalculateIcon,
  CheckCircle as MarkPaidIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
  Dashboard as DashboardIcon
} from '@mui/icons-material';
import JobRating from './JobRating';

const PaymentManagement = () => {
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [odooBillStatuses, setOdooBillStatuses] = useState({});
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMode, setFilterMode] = useState('all'); // 'all', 'urgent', 'overdue', 'needs-setup'
  const [sortMode, setSortMode] = useState('urgency'); // 'urgency', 'date', 'amount'
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    hasMore: true
  });
  
  // Date filtering state
  const [dateFilter, setDateFilter] = useState('all'); // 'all', 'today', 'week', 'month', 'custom'
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showDateFilters, setShowDateFilters] = useState(false);

  const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'http://localhost:3001';

  // Helper functions for date calculations
  const getDateRange = () => {
    const today = new Date();
    let calculatedStartDate = null;
    let calculatedEndDate = null;

    switch (dateFilter) {
      case 'today':
        calculatedStartDate = new Date(today.setHours(0, 0, 0, 0));
        calculatedEndDate = new Date(today.setHours(23, 59, 59, 999));
        break;
      case 'week':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay()); // Sunday
        startOfWeek.setHours(0, 0, 0, 0);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6); // Saturday
        endOfWeek.setHours(23, 59, 59, 999);
        calculatedStartDate = startOfWeek;
        calculatedEndDate = endOfWeek;
        break;
      case 'month':
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);
        calculatedStartDate = startOfMonth;
        calculatedEndDate = endOfMonth;
        break;
      case 'custom':
        calculatedStartDate = startDate;
        calculatedEndDate = endDate;
        break;
      default:
        // 'all' - no date filtering
        return { startDate: null, endDate: null };
    }

    return {
      startDate: calculatedStartDate ? calculatedStartDate.toISOString() : null,
      endDate: calculatedEndDate ? calculatedEndDate.toISOString() : null
    };
  };

  useEffect(() => {
    fetchJobsWithPayments(false);
  }, []);

  // Re-apply filters and sorting when data changes
  useEffect(() => {
    const filtered = applyFiltersAndSorting(jobs);
    setFilteredJobs(filtered);
  }, [jobs, searchTerm, filterMode, sortMode]);

  // Refetch jobs when date filters change
  useEffect(() => {
    fetchJobsWithPayments(false);
  }, [dateFilter, startDate, endDate]);

  const fetchJobsWithPayments = async (loadMore = false) => {
    if (loadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setPagination(prev => ({ ...prev, page: 1 }));
    }
    setError('');
    
    try {
      const currentPage = loadMore ? pagination.page + 1 : 1;
      const dateRange = getDateRange();
      
      // Build query parameters
      const params = new URLSearchParams({
        tenantId: '1',
        cityCode: 'MCO',
        limit: pagination.limit.toString(),
        page: currentPage.toString()
      });
      
      // Add date filters if they exist
      if (dateRange.startDate) {
        params.append('startDate', dateRange.startDate);
      }
      if (dateRange.endDate) {
        params.append('endDate', dateRange.endDate);
      }
      
      const response = await fetch(`${apiBase}/api/jobs/getJobs?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch jobs');
      }

      const data = await response.json();
      const fetchedJobs = data.data?.jobs || data.jobs || [];
      const totalJobs = data.data?.total || data.total || fetchedJobs.length;
      
      if (loadMore) {
        // Append new jobs to existing ones
        setJobs(prevJobs => [...prevJobs, ...fetchedJobs]);
      } else {
        // Replace jobs with new fetch
        setJobs(fetchedJobs);
      }
      
      // Update pagination info
      setPagination(prev => ({
        ...prev,
        page: currentPage,
        total: totalJobs,
        hasMore: fetchedJobs.length === pagination.limit && (currentPage * pagination.limit) < totalJobs
      }));
      
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError(err.message || 'Failed to fetch jobs');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchJobPayments = async (jobId) => {
    try {
      const response = await fetch(`${apiBase}/api/jobs/${jobId}/assignments/payment`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch job payments');
      }

      const data = await response.json();
      return data.data;
    } catch (err) {
      console.error('Error fetching job payments:', err);
      return null;
    }
  };

  const handleViewPayments = async (job) => {
    const paymentData = await fetchJobPayments(job.id);
    if (paymentData) {
      setSelectedJob({ ...job, paymentData });
      setPaymentDialogOpen(true);
      fetchOdooBillStatuses(paymentData.assignments || []);
    }
  };

  // Odoo vendor-bill chips — display-only, silently absent if the integration is off
  const fetchOdooBillStatuses = async (assignments) => {
    setOdooBillStatuses({});
    const ids = assignments.map(a => a?.id).filter(Boolean);
    if (ids.length === 0) return;
    try {
      const response = await fetch(`${apiBase}/api/admin/odoo/bill-status?assignmentIds=${ids.join(',')}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setOdooBillStatuses(data?.statuses || {});
    } catch {
      setOdooBillStatuses({});
    }
  };

  const renderOdooBillChip = (assignmentId) => {
    const bill = odooBillStatuses[assignmentId];
    if (!bill) return <Typography variant="body2" color="text.disabled">—</Typography>;
    if (bill.paymentState === 'paid' || bill.paymentState === 'in_payment') {
      return <Chip label="Payout recorded" color="success" size="small" variant="outlined" />;
    }
    if (bill.state === 'posted') {
      return <Chip label="Billed (posted)" color="info" size="small" variant="outlined" />;
    }
    return <Chip label="Billed (draft)" size="small" variant="outlined" />;
  };

  const handleEditPayment = (assignment) => {
    setEditingAssignment(assignment);
    setPaymentAmount(assignment.paymentAmount || '');
  };

  const handleSavePayment = async () => {
    if (!editingAssignment) return;

    try {
      const response = await fetch(`${apiBase}/api/jobs/assignments/${editingAssignment.id}/payment`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          paymentAmount: parseFloat(paymentAmount)
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update payment');
      }

      setSuccess('Payment amount updated successfully');
      setError('');
      setSnackbarOpen(true);
      setEditingAssignment(null);
      setPaymentAmount('');

      // Refresh the payment data for the current job dialog
      if (selectedJob) {
        const updatedPaymentData = await fetchJobPayments(selectedJob.id);
        if (updatedPaymentData) {
          setSelectedJob({ ...selectedJob, paymentData: updatedPaymentData });
        }
      }

      // Also refresh the main jobs list to reflect the payment amount change
      await fetchJobsWithPayments(false);
    } catch (err) {
      console.error('Error saving payment:', err);
      setError(err.message || 'Failed to save payment');
      setSuccess('');
      setSnackbarOpen(true);
    }
  };

  const handleMarkAsPaid = async (assignmentId) => {
    try {
      const response = await fetch(`${apiBase}/api/jobs/assignments/${assignmentId}/mark-paid`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to mark as paid');
      }

      setSuccess('Assignment marked as paid successfully');
      setError('');
      setSnackbarOpen(true);

      // Refresh the payment data for the current job dialog
      if (selectedJob) {
        const updatedPaymentData = await fetchJobPayments(selectedJob.id);
        if (updatedPaymentData) {
          setSelectedJob({ ...selectedJob, paymentData: updatedPaymentData });
        }
      }

      // Also refresh the main jobs list to reflect the payment status change
      await fetchJobsWithPayments(false);
    } catch (err) {
      console.error('Error marking assignment as paid:', err);
      setError(err.message || 'Failed to mark assignment as paid');
      setSuccess('');
      setSnackbarOpen(true);
    }
  };

  const handleRecalculatePayments = async (jobId) => {
    try {
      const response = await fetch(`${apiBase}/api/jobs/${jobId}/recalculate-payments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to recalculate payments');
      }

      setSuccess('Payments recalculated successfully');
      setError('');
      setSnackbarOpen(true);

      // Refresh the payment data for the current job dialog
      if (selectedJob && selectedJob.id === jobId) {
        const updatedPaymentData = await fetchJobPayments(jobId);
        if (updatedPaymentData) {
          setSelectedJob({ ...selectedJob, paymentData: updatedPaymentData });
        }
      }

      // Also refresh the main jobs list to reflect the recalculated payments
      await fetchJobsWithPayments(false);
    } catch (err) {
      console.error('Error recalculating payments:', err);
      setError(err.message || 'Failed to recalculate payments');
      setSuccess('');
      setSnackbarOpen(true);
    }
  };

  // Rating management methods
  const handleRatingSubmit = async (assignmentId, rating, notes) => {
    try {
      const response = await fetch(`${apiBase}/api/jobs/assignments/${assignmentId}/rating`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ rating, notes })
      });

      if (!response.ok) {
        throw new Error('Failed to submit rating');
      }

      setSuccess('Performance rating added successfully');
      setError('');
      setSnackbarOpen(true);

      // Refresh the payment data for the current job dialog
      if (selectedJob) {
        const updatedPaymentData = await fetchJobPayments(selectedJob.id);
        if (updatedPaymentData) {
          setSelectedJob({ ...selectedJob, paymentData: updatedPaymentData });
        }
      }

      // Also refresh the main jobs list
      await fetchJobsWithPayments(false);
    } catch (err) {
      console.error('Error submitting rating:', err);
      throw new Error(err.message || 'Failed to submit rating');
    }
  };

  const handleRatingUpdate = async (assignmentId, rating, notes) => {
    try {
      const response = await fetch(`${apiBase}/api/jobs/assignments/${assignmentId}/rating`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ rating, notes })
      });

      if (!response.ok) {
        throw new Error('Failed to update rating');
      }

      setSuccess('Performance rating updated successfully');
      setError('');
      setSnackbarOpen(true);

      // Refresh the payment data for the current job dialog
      if (selectedJob) {
        const updatedPaymentData = await fetchJobPayments(selectedJob.id);
        if (updatedPaymentData) {
          setSelectedJob({ ...selectedJob, paymentData: updatedPaymentData });
        }
      }

      // Also refresh the main jobs list
      await fetchJobsWithPayments(false);
    } catch (err) {
      console.error('Error updating rating:', err);
      throw new Error(err.message || 'Failed to update rating');
    }
  };

  const handleRatingDelete = async (assignmentId) => {
    try {
      const response = await fetch(`${apiBase}/api/jobs/assignments/${assignmentId}/rating`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete rating');
      }

      setSuccess('Performance rating removed successfully');
      setError('');
      setSnackbarOpen(true);

      // Refresh the payment data for the current job dialog
      if (selectedJob) {
        const updatedPaymentData = await fetchJobPayments(selectedJob.id);
        if (updatedPaymentData) {
          setSelectedJob({ ...selectedJob, paymentData: updatedPaymentData });
        }
      }

      // Also refresh the main jobs list
      await fetchJobsWithPayments(false);
    } catch (err) {
      console.error('Error deleting rating:', err);
      throw new Error(err.message || 'Failed to delete rating');
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'PAID':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'NOT_SET':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    // For job totalAmount, convert from cents to dollars if > 100, otherwise assume dollars
    // For paymentAmount, should already be in dollars from backend calculation
    const isJobTotal = amount > 500; // Assume job totals will be > $500 when in cents
    const dollarAmount = isJobTotal ? amount / 100 : amount;
    return `$${Number(dollarAmount).toFixed(2)}`;
  };

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  // Utility functions for urgency calculation
  const getJobUrgency = (job) => {
    if (!job) {
      return { level: 'none', priority: 0, label: 'N/A' };
    }
    
    const now = new Date();
    const scheduledDate = job.utcScheduledDate ? new Date(job.utcScheduledDate) : now;
    const daysSinceScheduled = Math.floor((now - scheduledDate) / (1000 * 60 * 60 * 24));
    
    // Check if job is completed
    const isCompleted = job.status === 'COMPLETED';
    
    // Check payment status
    const hasUnpaidAssignments = job.assignments?.some(assignment => 
      !assignment.paid && assignment.paymentAmount > 0
    );
    const hasMissingPaymentSetup = job.assignments?.some(assignment => 
      !assignment.paymentAmount || assignment.paymentAmount === 0
    );

    if (isCompleted && hasUnpaidAssignments && daysSinceScheduled > 7) {
      return { level: 'critical', priority: 4, label: 'OVERDUE' };
    }
    if (isCompleted && hasUnpaidAssignments && daysSinceScheduled > 1) {
      return { level: 'high', priority: 3, label: 'DUE SOON' };
    }
    if (hasMissingPaymentSetup) {
      return { level: 'medium', priority: 2, label: 'NEEDS SETUP' };
    }
    if (hasUnpaidAssignments) {
      return { level: 'low', priority: 1, label: 'PENDING' };
    }
    return { level: 'none', priority: 0, label: 'UP TO DATE' };
  };

  const getUrgencyColor = (urgency) => {
    if (!urgency || !urgency.level) {
      return '#4caf50'; // Green default
    }
    
    switch (urgency.level) {
      case 'critical': return '#d32f2f'; // Red
      case 'high': return '#f57c00'; // Orange
      case 'medium': return '#fbc02d'; // Yellow
      case 'low': return '#1976d2'; // Blue
      default: return '#4caf50'; // Green
    }
  };

  const getDaysSinceCompletion = (job) => {
    if (!job || job.status !== 'COMPLETED') return null;
    const now = new Date();
    const scheduledDate = job.utcScheduledDate ? new Date(job.utcScheduledDate) : now;
    return Math.floor((now - scheduledDate) / (1000 * 60 * 60 * 24));
  };

  // Enhanced job processing
  const enhanceJobsWithUrgency = (jobsList) => {
    return jobsList.map(job => ({
      ...job,
      urgency: getJobUrgency(job),
      daysSinceCompletion: getDaysSinceCompletion(job)
    }));
  };

  // Calculate summary metrics
  const calculateSummaryMetrics = (jobsList) => {
    const enhanced = enhanceJobsWithUrgency(jobsList);
    
    const overdue = enhanced.filter(job => job.urgency.level === 'critical');
    const dueSoon = enhanced.filter(job => job.urgency.level === 'high');
    const needsSetup = enhanced.filter(job => job.urgency.level === 'medium');
    
    const overdueAmount = overdue.reduce((sum, job) => 
      sum + (job.assignments?.reduce((assSum, ass) => 
        assSum + (ass.paid ? 0 : (ass.paymentAmount || 0)), 0) || 0), 0);
    
    const dueSoonAmount = dueSoon.reduce((sum, job) => 
      sum + (job.assignments?.reduce((assSum, ass) => 
        assSum + (ass.paid ? 0 : (ass.paymentAmount || 0)), 0) || 0), 0);

    return {
      overdue: { count: overdue.length, amount: overdueAmount },
      dueSoon: { count: dueSoon.length, amount: dueSoonAmount },
      needsSetup: { count: needsSetup.length, amount: 0 },
      total: enhanced.length
    };
  };

  // Filtering and sorting functions
  const applyFiltersAndSorting = (jobsList) => {
    let enhanced = enhanceJobsWithUrgency(jobsList);
    
    // Apply search filter first
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      enhanced = enhanced.filter(job => {
        const customerName = job.booking?.customerName || 
                            job.hcpRawData?.customer?.name || 
                            job.customer?.customerName || 
                            job.customer?.name || '';
        
        const maidNames = job.assignments ? 
          job.assignments.map(assignment => 
            assignment.maid ? `${assignment.maid.firstName} ${assignment.maid.lastName}` : ''
          ).join(' ') : '';

        const jobNumber = job.jobNumber || '';

        return customerName.toLowerCase().includes(searchLower) ||
               maidNames.toLowerCase().includes(searchLower) ||
               jobNumber.toLowerCase().includes(searchLower);
      });
    }

    // Apply urgency filter
    if (filterMode !== 'all') {
      enhanced = enhanced.filter(job => {
        switch (filterMode) {
          case 'urgent':
            return ['critical', 'high'].includes(job.urgency.level);
          case 'overdue':
            return job.urgency.level === 'critical';
          case 'needs-setup':
            return job.urgency.level === 'medium';
          default:
            return true;
        }
      });
    }

    // Apply sorting
    enhanced.sort((a, b) => {
      switch (sortMode) {
        case 'urgency':
          return b.urgency.priority - a.urgency.priority;
        case 'date':
          return new Date(b.utcScheduledDate) - new Date(a.utcScheduledDate);
        case 'amount':
          const aAmount = a.assignments?.reduce((sum, ass) => sum + (ass.paymentAmount || 0), 0) || 0;
          const bAmount = b.assignments?.reduce((sum, ass) => sum + (ass.paymentAmount || 0), 0) || 0;
          return bAmount - aAmount;
        default:
          return 0;
      }
    });

    return enhanced;
  };

  // Bulk action handlers
  const handleBulkRecalculatePayments = async () => {
    const jobsToRecalculate = filteredJobs.filter(job => 
      job.assignments && job.assignments.length > 0
    );

    if (jobsToRecalculate.length === 0) {
      setError('No jobs with assignments found to recalculate');
      setSuccess('');
      setSnackbarOpen(true);
      return;
    }

    try {
      const recalculatePromises = jobsToRecalculate.map(job =>
        fetch(`${apiBase}/api/jobs/${job.id}/recalculate-payments`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        })
      );

      const results = await Promise.allSettled(recalculatePromises);
      const successful = results.filter(result => result.status === 'fulfilled').length;
      const failed = results.length - successful;

      if (failed === 0) {
        setSuccess(`Successfully recalculated payments for all ${successful} jobs`);
      } else {
        setSuccess(`Recalculated ${successful} jobs successfully, ${failed} failed`);
      }
      
      setError('');
      setSnackbarOpen(true);
      await fetchJobsWithPayments(false);
    } catch (err) {
      console.error('Error in bulk recalculate:', err);
      setError('Failed to recalculate payments');
      setSuccess('');
      setSnackbarOpen(true);
    }
  };

  const handleBulkMarkAsPaid = async () => {
    const assignmentsToMarkPaid = [];
    
    filteredJobs.forEach(job => {
      if (job.assignments) {
        job.assignments.forEach(assignment => {
          if (!assignment.paid && assignment.paymentAmount > 0) {
            assignmentsToMarkPaid.push(assignment);
          }
        });
      }
    });

    if (assignmentsToMarkPaid.length === 0) {
      setError('No unpaid assignments with payment amounts found');
      setSuccess('');
      setSnackbarOpen(true);
      return;
    }

    try {
      const markPaidPromises = assignmentsToMarkPaid.map(assignment =>
        fetch(`${apiBase}/api/jobs/assignments/${assignment.id}/mark-paid`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        })
      );

      const results = await Promise.allSettled(markPaidPromises);
      const successful = results.filter(result => result.status === 'fulfilled').length;
      const failed = results.length - successful;

      if (failed === 0) {
        setSuccess(`Successfully marked ${successful} assignments as paid`);
      } else {
        setSuccess(`Marked ${successful} assignments as paid, ${failed} failed`);
      }
      
      setError('');
      setSnackbarOpen(true);
      await fetchJobsWithPayments(false);
    } catch (err) {
      console.error('Error in bulk mark as paid:', err);
      setError('Failed to mark assignments as paid');
      setSuccess('');
      setSnackbarOpen(true);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2">
          Payment Management
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={() => fetchJobsWithPayments(false)} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Summary Dashboard Cards */}
      {jobs.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {(() => {
            const metrics = calculateSummaryMetrics(jobs);
            return (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ 
                    backgroundColor: metrics.overdue.count > 0 ? 'rgba(211, 47, 47, 0.1)' : 'rgba(76, 175, 80, 0.1)',
                    border: `1px solid ${metrics.overdue.count > 0 ? '#d32f2f' : '#4caf50'}`
                  }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <ErrorIcon sx={{ color: '#d32f2f' }} />
                        <Typography variant="h6" color="#d32f2f">
                          {metrics.overdue.count}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Overdue Payments
                      </Typography>
                      <Typography variant="h6" color="#d32f2f">
                        {formatCurrency(metrics.overdue.amount)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ 
                    backgroundColor: metrics.dueSoon.count > 0 ? 'rgba(245, 124, 0, 0.1)' : 'rgba(76, 175, 80, 0.1)',
                    border: `1px solid ${metrics.dueSoon.count > 0 ? '#f57c00' : '#4caf50'}`
                  }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <WarningIcon sx={{ color: '#f57c00' }} />
                        <Typography variant="h6" color="#f57c00">
                          {metrics.dueSoon.count}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Due Soon
                      </Typography>
                      <Typography variant="h6" color="#f57c00">
                        {formatCurrency(metrics.dueSoon.amount)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ 
                    backgroundColor: metrics.needsSetup.count > 0 ? 'rgba(251, 192, 45, 0.1)' : 'rgba(76, 175, 80, 0.1)',
                    border: `1px solid ${metrics.needsSetup.count > 0 ? '#fbc02d' : '#4caf50'}`
                  }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <ScheduleIcon sx={{ color: '#fbc02d' }} />
                        <Typography variant="h6" color="#fbc02d">
                          {metrics.needsSetup.count}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Need Setup
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Missing Payment Info
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ 
                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                    border: '1px solid #1976d2'
                  }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <DashboardIcon sx={{ color: '#1976d2' }} />
                        <Typography variant="h6" color="#1976d2">
                          {metrics.total}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Total Jobs
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        In Current View
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </>
            );
          })()}
        </Grid>
      )}

      {/* Filter and Sort Controls */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap" alignItems="center">
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Filter</InputLabel>
          <Select
            value={filterMode}
            label="Filter"
            onChange={(e) => setFilterMode(e.target.value)}
            startAdornment={<FilterListIcon sx={{ mr: 1, color: 'action.active' }} />}
          >
            <MenuItem value="all">All Jobs</MenuItem>
            <MenuItem value="urgent">🚨 Urgent Actions</MenuItem>
            <MenuItem value="overdue">🔴 Overdue</MenuItem>
            <MenuItem value="needs-setup">⚠️ Needs Setup</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Sort by</InputLabel>
          <Select
            value={sortMode}
            label="Sort by"
            onChange={(e) => setSortMode(e.target.value)}
            startAdornment={<SortIcon sx={{ mr: 1, color: 'action.active' }} />}
          >
            <MenuItem value="urgency">🔥 Urgency</MenuItem>
            <MenuItem value="date">📅 Date</MenuItem>
            <MenuItem value="amount">💰 Amount</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Date Range</InputLabel>
          <Select
            value={dateFilter}
            label="Date Range"
            onChange={(e) => setDateFilter(e.target.value)}
            startAdornment={<ScheduleIcon sx={{ mr: 1, color: 'action.active' }} />}
          >
            <MenuItem value="all">All Time</MenuItem>
            <MenuItem value="today">Today</MenuItem>
            <MenuItem value="week">This Week</MenuItem>
            <MenuItem value="month">This Month</MenuItem>
            <MenuItem value="custom">Custom Range</MenuItem>
          </Select>
        </FormControl>

        {dateFilter === 'custom' && (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={setStartDate}
              renderInput={(params) => (
                <TextField {...params} size="small" sx={{ width: 140 }} />
              )}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={setEndDate}
              renderInput={(params) => (
                <TextField {...params} size="small" sx={{ width: 140 }} />
              )}
            />
          </LocalizationProvider>
        )}

        {/* Quick Action Buttons */}
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<CalculateIcon />}
            onClick={() => handleBulkRecalculatePayments()}
            disabled={filteredJobs.length === 0}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Recalc All
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<MarkPaidIcon />}
            onClick={() => handleBulkMarkAsPaid()}
            disabled={filteredJobs.filter(job => 
              job.assignments?.some(assignment => 
                !assignment.paid && assignment.paymentAmount > 0
              )
            ).length === 0}
            color="success"
            sx={{ whiteSpace: 'nowrap' }}
          >
            Mark All Paid
          </Button>
        </Box>
      </Box>

      {/* Search Field */}
      <Box mb={3}>
        <TextField
          fullWidth
          placeholder="Search by customer name, technician name, or job number..."
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton onClick={clearSearch} size="small">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 600 }}
        />
        {searchTerm && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {filteredJobs.length} result{filteredJobs.length !== 1 ? 's' : ''} found
            {filteredJobs.length !== jobs.length && ` (filtered from ${jobs.length} total jobs)`}
          </Typography>
        )}
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2,
            backgroundColor: 'rgba(211, 47, 47, 0.1)', // Explicit red background
            color: 'rgba(211, 47, 47, 1)', // Explicit red text
            '& .MuiAlert-icon': {
              color: 'rgba(211, 47, 47, 1)' // Explicit red icon
            }
          }}
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {filteredJobs.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <Typography variant="body1" color="text.secondary">
                {searchTerm ? 'No jobs found matching your search criteria.' : 'No jobs available.'}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Priority</TableCell>
                    <TableCell>Job Number</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Total Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Scheduled Date</TableCell>
                    <TableCell>Assigned Technicians & Payments</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredJobs.map((job) => (
                <TableRow 
                  key={job.id}
                  sx={{
                    backgroundColor: job.urgency?.level === 'critical' ? 'rgba(211, 47, 47, 0.05)' :
                                    job.urgency?.level === 'high' ? 'rgba(245, 124, 0, 0.05)' :
                                    job.urgency?.level === 'medium' ? 'rgba(251, 192, 45, 0.05)' :
                                    'rgba(255, 255, 255, 0)', // Use transparent rgba instead of 'transparent'
                    '&:hover': {
                      backgroundColor: job.urgency?.level === 'critical' ? 'rgba(211, 47, 47, 0.1)' :
                                      job.urgency?.level === 'high' ? 'rgba(245, 124, 0, 0.1)' :
                                      job.urgency?.level === 'medium' ? 'rgba(251, 192, 45, 0.1)' :
                                      'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {job.urgency?.level === 'critical' && <ErrorIcon sx={{ color: '#d32f2f', fontSize: 20 }} />}
                      {job.urgency?.level === 'high' && <WarningIcon sx={{ color: '#f57c00', fontSize: 20 }} />}
                      {job.urgency?.level === 'medium' && <ScheduleIcon sx={{ color: '#fbc02d', fontSize: 20 }} />}
                      {job.urgency?.level === 'low' && <PendingIcon sx={{ color: '#1976d2', fontSize: 20 }} />}
                      {(!job.urgency?.level || job.urgency?.level === 'none') && <CheckCircleOutlineIcon sx={{ color: '#4caf50', fontSize: 20 }} />}
                      <Chip
                        label={job.urgency?.label || 'N/A'}
                        size="small"
                        sx={{
                          backgroundColor: getUrgencyColor(job.urgency || null),
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.7rem'
                        }}
                      />
                    </Box>
                    {job.daysSinceCompletion !== null && job.daysSinceCompletion !== undefined && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {job.daysSinceCompletion === 0 ? 'Completed today' : 
                         job.daysSinceCompletion === 1 ? 'Completed 1 day ago' :
                         `Completed ${job.daysSinceCompletion} days ago`}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{job?.jobNumber || 'N/A'}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {job.booking?.customerName || 
                       job.hcpRawData?.customer?.name || 
                       job.customer?.customerName || 
                       job.customer?.name || 
                       'Unknown Customer'}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatCurrency(job?.totalAmount || 0)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={job?.status || 'Unknown'} 
                      color={job?.status === 'COMPLETED' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {job?.utcScheduledDate ? new Date(job.utcScheduledDate).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {job?.assignments && job.assignments.length > 0 ? (
                      <Box>
                        {job.assignments.map((assignment, index) => (
                          <Box key={assignment.id} sx={{ mb: index < job.assignments.length - 1 ? 1 : 0 }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2" component="span">
                                {assignment.maid ? `${assignment.maid.firstName} ${assignment.maid.lastName}` : 'Unknown Technician'}
                              </Typography>
                              <Chip 
                                label={assignment.paid ? 'PAID' : 'PENDING'}
                                color={assignment.paid ? 'success' : 'warning'}
                                size="small"
                                sx={{ fontSize: '0.7rem', height: '20px' }}
                              />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                              Share: {formatCurrency(assignment.paymentAmount || 0)}
                              {assignment.paidDate && (
                                <span> • Paid: {new Date(assignment.paidDate).toLocaleDateString()}</span>
                              )}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No technicians assigned</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      startIcon={<PaymentIcon />}
                      onClick={() => job && handleViewPayments(job)}
                      variant="outlined"
                      size="small"
                      disabled={!job}
                    >
                      Manage Payments
                    </Button>
                  </TableCell>
                </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          {/* Load More Button */}
          {pagination.hasMore && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Button
                variant="outlined"
                onClick={() => fetchJobsWithPayments(true)}
                disabled={loadingMore}
                startIcon={loadingMore ? <CircularProgress size={16} /> : null}
                sx={{ minWidth: 200 }}
              >
                {loadingMore ? 'Loading More...' : `Load More Jobs (${pagination.total - jobs.length} remaining)`}
              </Button>
            </Box>
          )}
        </>
      )}

      {/* Pagination Info */}
      {jobs.length > 0 && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
          <Typography variant="body2" color="text.secondary">
            Showing {jobs.length} of {pagination.total} jobs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Page {pagination.page} {pagination.hasMore ? `(${Math.ceil(pagination.total / pagination.limit)} total pages)` : '(Last page)'}
          </Typography>
        </Box>
      )}

      {/* Payment Details Dialog */}
      <Dialog 
        open={paymentDialogOpen} 
        onClose={() => setPaymentDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">
                Payment Details - Job {selectedJob?.jobNumber}
              </Typography>
              {selectedJob && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Customer: {selectedJob.booking?.customerName || 
                            selectedJob.hcpRawData?.customer?.name || 
                            selectedJob.customer?.customerName || 
                            selectedJob.customer?.name || 
                            'Unknown Customer'}
                </Typography>
              )}
            </Box>
            <Button
              startIcon={<CalculateIcon />}
              onClick={() => handleRecalculatePayments(selectedJob?.id)}
              variant="outlined"
              size="small"
            >
              Recalculate
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedJob?.paymentData && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Job Summary
                      </Typography>
                      <Typography>
                        Total Amount: {formatCurrency(selectedJob?.paymentData?.job?.totalAmount || 0)}
                      </Typography>
                      <Typography>
                        Status: {selectedJob?.paymentData?.job?.status || 'Unknown'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Payment Summary
                      </Typography>
                      <Typography>
                        Total Payments: {formatCurrency(selectedJob?.paymentData?.summary?.totalPayments || 0)}
                      </Typography>
                      <Typography>
                        Paid: {selectedJob?.paymentData?.summary?.paidAssignments || 0} / {selectedJob?.paymentData?.summary?.totalAssignments || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Typography variant="h6" gutterBottom>
                Assignment Payments
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Technician</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Payment Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Paid Date</TableCell>
                      <TableCell>Odoo</TableCell>
                      <TableCell>Performance Rating</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(selectedJob?.paymentData?.assignments || []).map((assignment) => (
                      <TableRow key={assignment?.id || Math.random()}>
                        <TableCell>{assignment?.maidName || 'Unknown'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={assignment?.role || 'Unknown'} 
                            color={assignment?.role === 'LEAD' ? 'primary' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {editingAssignment?.id === assignment?.id ? (
                            <TextField
                              value={paymentAmount}
                              onChange={(e) => setPaymentAmount(e.target.value)}
                              type="number"
                              size="small"
                              inputProps={{ step: "0.01" }}
                            />
                          ) : (
                            formatCurrency(assignment?.paymentAmount || 0)
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={assignment?.paymentStatus || 'Unknown'}
                            color={getPaymentStatusColor(assignment?.paymentStatus)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {assignment?.paidDate ?
                            new Date(assignment.paidDate).toLocaleDateString() :
                            'Not paid'
                          }
                        </TableCell>
                        <TableCell>
                          {renderOdooBillChip(assignment?.id)}
                        </TableCell>
                        <TableCell>
                          <JobRating
                            assignment={assignment || {}}
                            onRatingSubmit={handleRatingSubmit}
                            onRatingUpdate={handleRatingUpdate}
                            onRatingDelete={handleRatingDelete}
                          />
                        </TableCell>
                        <TableCell>
                          {editingAssignment?.id === assignment?.id ? (
                            <Box>
                              <Button 
                                onClick={handleSavePayment}
                                size="small"
                                variant="contained"
                                color="primary"
                              >
                                Save
                              </Button>
                              <Button 
                                onClick={() => setEditingAssignment(null)}
                                size="small"
                                variant="outlined"
                              >
                                Cancel
                              </Button>
                            </Box>
                          ) : (
                            <Box>
                              <IconButton
                                onClick={() => handleEditPayment(assignment || {})}
                                size="small"
                                title="Edit Payment"
                              >
                                <EditIcon />
                              </IconButton>
                              {!assignment?.paid && assignment?.paymentAmount && (
                                <IconButton
                                  onClick={() => handleMarkAsPaid(assignment?.id)}
                                  size="small"
                                  color="success"
                                  title="Mark as Paid"
                                >
                                  <MarkPaidIcon />
                                </IconButton>
                              )}
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen && success && !error}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={success}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: '#2e7d32', // Green background
            color: '#ffffff'
          }
        }}
      />
      
      {/* Error Snackbar */}
      <Snackbar
        open={snackbarOpen && error && !success}
        autoHideDuration={6000}
        onClose={() => {
          setSnackbarOpen(false);
          setError('');
        }}
        message={error}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: '#d32f2f', // Red background
            color: '#ffffff'
          }
        }}
      />
    </Paper>
  );
};

export default PaymentManagement;